import {Action, Module, Mutation, VuexModule} from 'vuex-module-decorators';
import {mergeAndAdd} from '../mergeAndAdd';
import {Sorting} from '../../helpers';
import type {TFeedbackStatus} from '../constants';
import {IBaseResponse, WebService} from '../../services';
import type {ITicket, TMaybeTicket, TTicketId, TTicketIdFunction, TTicketResponse} from '../order';
import {TicketsStore, IHistoryEntry} from '../../store';


@Module({name: 'history', namespaced: true})
class HistoryModule extends VuexModule {
	tickets: ITicket[] = [];
	historyEntries: IHistoryEntry[] = [];

	/**
	 * We're unable to communicate between VisitDetails and the FeedbackForm QDialog
	 * using events, but need to display a spinner in the former while feedback is
	 * submitted to the server by the latter. Use this property as a shared flag.
	 */
	submittingFeedback = false;

	get sortedHistory(): IHistoryEntry[] {
		return [...this.historyEntries].sort(Sorting.sortHistoryEntriesByCheckoutDateDesc);
	}

	get ticketById(): TTicketIdFunction<TMaybeTicket> {
		return (ticketId: TTicketId) => {
			return this.tickets.find(
				(ticket) => ticket?.ticketId === ticketId
			) ?? null;
		};
	}

	@Action
	async fetchHistory() {
		const response = await WebService.authenticatedClient.get<IBaseResponse<{history: IHistoryEntry[]}>>('history');
		this.addHistoryEntry(response.data.data.history);
	}

	@Action
	async fetchTicket(payload: {locationId: string; ticketId: string}) {
		const response = await WebService.getOrderingClient(payload.locationId).get<TTicketResponse>(`/tickets/${payload.ticketId}`);
		this.addTicket(response.data.data.ticket);
	}

	@Action
	async submitFeedback(payload: {locationId: string; ticketId: string; status: TFeedbackStatus; note: string}) {
		this.setSubmittingFeedback();
		const response = await WebService.getOrderingClient(payload.locationId).post<IBaseResponse<{ticket: ITicket}>>(`tickets/${payload.ticketId}/feedback`, {
			status: payload.status,
			note: payload.note
		});
		this.addTicket(response.data.data.ticket);
		if(TicketsStore.latestTicket && TicketsStore.latestTicket.ticketId === response.data.data.ticket.ticketId) {
			TicketsStore.setLatestTicket(response.data.data.ticket);
		}
		this.clearSubmittingFeedback();
	}

	@Mutation
	public clearHistory() {
		this.tickets = [];
		this.historyEntries = [];
	}

	@Mutation
	public addTicket(ticket: ITicket | ITicket[]) {
		mergeAndAdd(this.tickets, ticket, ['ticketId']);
	}

	@Mutation
	public addHistoryEntry(historyEntry: IHistoryEntry | IHistoryEntry[]) {
		mergeAndAdd(this.historyEntries, historyEntry, ['ticketId']);
	}

	@Mutation
	public clearSubmittingFeedback() {
		this.submittingFeedback = false;
	}

	@Mutation
	public setSubmittingFeedback() {
		this.submittingFeedback = true;
	}
}


export {
	HistoryModule
};
